/* eslint-disable @typescript-eslint/no-explicit-any -- generic */
import { useMemo } from "react";

import { FormControl, FormLabel, Stack } from "@mui/material";
import type { ErrorInfo } from "remult";
import {
  AutocompleteInput,
  CheckboxInput,
  NormalInput,
  SelectInput,
  TextAreaInput,
} from "./FormInput";

export type FieldConfig = {
  type:
    | "text"
    | "textarea"
    | "number"
    | "checkbox"
    | "date"
    | "password"
    | "select"
    | "autocomplete";
} & {
  caption: string;
  required?: boolean;
  disabled?: boolean;
  options?: { value: string; content: string }[];
  key: string;
  displayValue: (val: any) => string | Promise<string>;
  name?: string;
};

export interface FieldInGroupProps {
  field: FieldConfig;
  value: string;
  setValue: (newValue: string) => void;
}

export default function FormGroup(props: {
  fields: Record<string, Partial<FieldConfig>>;
  state: Record<string, string>;
  setState: (newState: Record<string, string>) => void;
  error: ErrorInfo<unknown> | undefined;
  setError: (e: ErrorInfo<unknown>) => void;
  errorMessages?: string[];
}) {
  const { error, setError, fields: formFields, setState, state } = props;
  const fields = useMemo(() => {
    const result: FieldConfig[] = [];
    for (const key in formFields) {
      if (Object.prototype.hasOwnProperty.call(formFields, key)) {
        const element = formFields[key];
        result.push({
          type: "text",
          caption: capitalize(key),
          key,
          ...element,
        } as any);
      }
    }
    return result;
  }, []);

  function capitalize(str: string) {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  function getValueAsString<T extends Record<string, any>>(
    obj: T,
    key: string,
  ) {
    return String(obj[key]);
  }

  return (
    <Stack direction="column" gap={4}>
      {fields.map((field) => {
        // @ts-expect-error - fieldError is a string
        const fieldError = error?.modelState?.[field.key];
        const args: FieldInGroupProps = {
          field,
          value: state[field.key] || "",
          setValue: (value) => {
            setState({ ...state, [field.key]: value });
            if (fieldError) {
              setError({
                ...error!,
                modelState: {
                  ...error!.modelState,
                  [field.key]: undefined,
                },
              });
            }
          },
        };
        function RenderField() {
          if (field.type)
            switch (field.type) {
              case "checkbox":
                return (
                  <>
                    <CheckboxInput {...args} />
                    {error && field.name && (
                      <div>{getValueAsString(error, field.name)}</div>
                    )}
                  </>
                );
              case "select":
                return (
                  <>
                    <SelectInput {...args} />
                    {error && field.name && (
                      <div>{getValueAsString(error, field.name)}</div>
                    )}
                  </>
                );
              case "autocomplete":
                return <AutocompleteInput {...args} />;
              case "textarea":
                return <TextAreaInput {...args} />;
              default:
                return (
                  <>
                    <NormalInput {...args} />
                    {error && field.name && (
                      <div>{getValueAsString(error, field.name)}</div>
                    )}
                  </>
                );
            }
        }

        return (
          <Stack width="100%" alignItems="center" key={field.key}>
            <FormControl fullWidth>
              <FormLabel
                htmlFor={field.key}
                className={fieldError ? "text-destructive" : undefined}
              >
                {field.caption}
              </FormLabel>
              {RenderField()}
              {fieldError ? (
                <div className="text-destructive">{String(fieldError)}</div>
              ) : null}
            </FormControl>
          </Stack>
        );
      })}
      {/* <pre>{JSON.stringify(props.state, undefined, 2)}</pre> */}
    </Stack>
  );
}
/* eslint-enable @typescript-eslint/no-explicit-any -- generic */
